import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function sortKey(numConta) {
  const s = String(numConta || '').trim();
  const m = s.match(/^(\d+)/);
  const num = m ? parseInt(m[1], 10) : NaN;
  const sufMatch = s.match(/-(.*)$/);
  const suffix = sufMatch ? sufMatch[1].toString().toUpperCase() : '';
  return { raw: s, num, suffix };
}

function SaldoProgramaModal({ show, onClose, idPrograma, programaDescricao = '', ano = '' }) {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAno, setLoadedAno] = useState('');

  useEffect(() => {
    if (!show || idPrograma == null || idPrograma === '') {
      setRows([]);
      return;
    }
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');
    const anoCorrente = ano || localStorage.getItem('ano') || '';
    const programa = String(idPrograma);

    setIsLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL.SRES}/search`, {
        params: { idEscola: cnpj, ano: anoCorrente },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      }),
      axios.get(`${API_BASE_URL.CONTA}/findByEscolaAndPrograma`, {
        params: { idEscola: cnpj, idPrograma: programa },
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      }),
    ])
      .then(([sresResp, contaResp]) => {
        const sresList = Array.isArray(sresResp.data) ? sresResp.data : [];
        const contas = Array.isArray(contaResp.data) ? contaResp.data : [];

        const lista = sresList
          .filter(s => String(s.idPrograma) === programa)
          .map(s => {
            const k = String(s.idConta != null ? s.idConta : '');
            const contaObj = contas.find(c => String(c.numConta) === k || String(c.idConta) === k);
            const custeio = Number(s.vlrC || 0);
            const capital = Number(s.vlrK || 0);
            return {
              key: k,
              numConta: contaObj ? contaObj.numConta : k,
              label: contaObj ? (contaObj.nomeConta || contaObj.numConta || contaObj.idConta) : k,
              debitar: contaObj ? contaObj.debitar : '',
              custeio,
              capital,
              total: custeio + capital,
            };
          });

        lista.sort((a, b) => {
          const ka = sortKey(a.numConta);
          const kb = sortKey(b.numConta);
          if (!isNaN(ka.num) && !isNaN(kb.num) && ka.num !== kb.num) return ka.num - kb.num;
          if (!isNaN(ka.num) && !isNaN(kb.num)) {
            const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
            if (s !== 0) return s;
          }
          return ka.raw.localeCompare(kb.raw, 'pt-BR', { sensitivity: 'base' });
        });

        setRows(lista);
        setLoadedAno(anoCorrente);
      })
      .catch(() => {
        setRows([]);
      })
      .finally(() => setIsLoading(false));
  }, [show, idPrograma, ano]);

  if (!show) return null;

  const totalCusteio = rows.reduce((acc, r) => acc + r.custeio, 0);
  const totalCapital = rows.reduce((acc, r) => acc + r.capital, 0);
  const totalGeral = rows.reduce((acc, r) => acc + r.total, 0);

  const efetivas = rows.filter(r => r.debitar === 'SIM');
  const totalEfetivoCusteio = efetivas.reduce((acc, r) => acc + r.custeio, 0);
  const totalEfetivoCapital = efetivas.reduce((acc, r) => acc + r.capital, 0);
  const totalEfetivoGeral = efetivas.reduce((acc, r) => acc + r.total, 0);

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', inset: 0 }}>
      <div className="modal-dialog modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header text-white" style={{ background: '#198754', padding: '0.35rem 1rem' }}>
            <h6 className="modal-title mb-0 small">
              <i className="bi bi-piggy-bank me-2"></i>Saldo SRES por Contas — {programaDescricao || idPrograma}
              {loadedAno ? ` (${loadedAno})` : ''}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {isLoading ? (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm"></span>
              </div>
            ) : rows.length === 0 ? (
              <p className="text-muted text-center mb-0 py-3">Nenhuma conta com saldo SRES para este programa.</p>
            ) : (
              <table className="table table-sm table-hover mb-0" style={{ fontSize: '.85rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Conta</th>
                    <th className="text-end">Custeio</th>
                    <th className="text-end">Capital</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.key}>
                      <td>{idx + 1}</td>
                      <td>{r.label}</td>
                      <td className="text-end">{formatCurrency(r.custeio)}</td>
                      <td className="text-end">{formatCurrency(r.capital)}</td>
                      <td className="text-end fw-bold text-primary">{formatCurrency(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold" style={{ borderTop: '2px solid rgba(13,110,253,.25)' }}>
                    <td colSpan="2">Total</td>
                    <td className="text-end">{formatCurrency(totalCusteio)}</td>
                    <td className="text-end">{formatCurrency(totalCapital)}</td>
                    <td className="text-end text-primary">{formatCurrency(totalGeral)}</td>
                  </tr>
                  <tr className="fw-bold" style={{ borderTop: '2px solid rgba(25,135,84,.35)', background: 'rgba(25,135,84,.12)', color: '#146c43' }}>
                    <td colSpan="2"><i className="bi bi-arrow-up-circle-fill me-1"></i>Disponível para Despesas</td>
                    <td className="text-end">{formatCurrency(totalEfetivoCusteio)}</td>
                    <td className="text-end">{formatCurrency(totalEfetivoCapital)}</td>
                    <td className="text-end" style={{ color: '#146c43' }}>{formatCurrency(totalEfetivoGeral)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
          <div className="modal-footer py-2">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Fechar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SaldoProgramaModal;